import { FormikProps } from 'formik';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { getErrorMessage, getValidationErrorDetails } from '@/utils/errorHandling';

interface QuestionFormSubmitProps {
  formik: FormikProps<QuestionFormValues>;
  isEditable: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  questionEditMessage?: string | null;
  onPreview: (values: QuestionFormValues) => void;
  validationSchema: any;
}

export function QuestionFormSubmit({
  formik,
  isEditable,
  isPending,
  isError,
  error,
  questionEditMessage,
  onPreview,
  validationSchema,
}: QuestionFormSubmitProps) {
  return (
    <>
      {/* Info message for non-editable questions */}
      {!isEditable && questionEditMessage && (
        <Alert className="bg-blue-50 border-blue-200">
          <AlertDescription className="text-blue-800">{questionEditMessage}</AlertDescription>
        </Alert>
      )}

      {/* Server-side validation errors */}
      {isError && error && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="font-semibold mb-1">Failed to save question</div>
            <div className="text-sm">{getErrorMessage(error)}</div>
            {/* Display additional validation details if available */}
            {(() => {
              const validationErrors = getValidationErrorDetails(error);
              if (!validationErrors) return null;

              return (
                <ul className="mt-2 text-sm list-disc list-inside">
                  {Object.entries(validationErrors).map(([field, message]) => (
                    <li key={field}>
                      <span className="font-medium">{field}:</span> {String(message)}
                    </li>
                  ))}
                </ul>
              );
            })()}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-3">
        <Button
          type="button"
          data-testid="question-preview-button"
          variant="outline"
          onClick={() => onPreview(formik.values)}
          className="flex-1"
        >
          <Eye className="mr-2 h-4 w-4" />
          Preview
        </Button>
        {isEditable && (
          <Button
            type="button"
            data-testid="question-submit-button"
            disabled={isPending}
            className="flex-1"
            onClick={async () => {
              try {
                await validationSchema.validate(formik.values, {
                  abortEarly: false,
                });
                formik.handleSubmit();
              } catch (err) {
                const validationError = err as { errors?: string[] };
                const errors = validationError.errors ?? [
                  err instanceof Error ? err.message : 'Validation failed',
                ];
                toast.error('Validation failed', {
                  description: errors.length > 1 ? errors.map(e => `• ${e}`).join('\n') : errors[0],
                  duration: 6000,
                });
              }
            }}
          >
            {isPending ? 'Saving...' : 'Save Question'}
          </Button>
        )}
      </div>
    </>
  );
}
